/**
 * AlphaScout Picks Table — Phase 4
 * Displays the current stable top picks with composite score, entry/SL/target,
 * and factor breakdown sparkline.
 */
import React from 'react';
import { TrendingUp, TrendingDown, Target } from 'lucide-react';
import { scoreColor, factorMetaSingular, formatPct, formatMoney, formatCr } from './utils';
import { SortHeader, useSortable } from './SortableHeader';
import { HoverCard, HoverCardTrigger, HoverCardContent } from '../ui/hover-card';
import MiniFactorRadar from './MiniFactorRadar';
import EventBadge from './EventBadge';
import ActionabilityBadge from './ActionabilityBadge';

const RR_TOOLTIP = 'Reward to Risk — for every 1 INR risked, potential reward in INR. Format "X:1".';

// Session 142c — label priority for sorting. FRESH is most actionable, so
// when users sort by status, FRESH > PULLBACK > NO_POSITION > TOO_LATE.
const ACTIONABILITY_SORT_PRIORITY = {
  FRESH: 0,
  ENTER_ON_PULLBACK: 1,
  NO_POSITION: 2,
  TOO_LATE: 3,
};

/** Extract a numeric sort key from an actionability object. Falls to the
 *  end of the list when the label is missing. */
const actionabilitySortKey = (pick) => {
  const label = pick?.actionability?.label;
  return ACTIONABILITY_SORT_PRIORITY[label] ?? 99;
};

const FactorBar = ({ name, value }) => {
  // Session 147j+16 — fall back to a placeholder so an unknown factor
  // key (e.g. a newly-introduced backend factor) doesn't crash the
  // entire AlphaScout tab.
  const meta = factorMetaSingular[name] || { label: name, color: '#94a3b8' };
  return (
    <div className="flex items-center gap-1" title={`${meta.label}: ${value}`}>
      <span className="text-[9px] text-muted-foreground uppercase w-9 font-mono">
        {meta.label.slice(0, 4)}
      </span>
      <div className="flex-1 h-1.5 bg-gray-800 rounded-sm overflow-hidden">
        <div
          className="h-full rounded-sm"
          style={{ width: `${value}%`, backgroundColor: meta.color }}
        />
      </div>
      <span className="text-[10px] font-mono text-foreground w-6 text-right">
        {Math.round(value)}
      </span>
    </div>
  );
};

const PicksTable = ({ picks = [], onRowClick, selectedSymbol }) => {
  const { sorted, field, dir, toggle } = useSortable(picks, {
    initialField: 'composite_score',
    initialDir: 'desc',
    accessors: {
      // Session 142c — sort Status column by actionability priority so
      // users can group FRESH picks at the top with a single click.
      actionability: actionabilitySortKey,
    },
  });

  if (!picks.length) {
    return (
      <div className="bg-surface border border-border p-8 text-center" data-testid="picks-empty">
        <Target className="w-8 h-8 mx-auto mb-3 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          No picks yet. Run scoring + rebalance to see top stocks.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-border overflow-hidden" data-testid="picks-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-highlight border-b border-border text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-2 py-2 text-left w-8">#</th>
              <SortHeader label="Symbol" sortKey="symbol" active={field} dir={dir} onToggle={toggle} className="min-w-[110px]" />
              <SortHeader
                label="Status"
                sortKey="actionability"
                active={field}
                dir={dir}
                onToggle={toggle}
                className="min-w-[100px]"
                tooltip="Pick actionability. FRESH: enter at market. PULLBACK: wait for dip. TOO LATE: R:R broken."
              />
              <SortHeader label="Sector" sortKey="sector" active={field} dir={dir} onToggle={toggle} className="min-w-[130px]" />
              <SortHeader label="Score" sortKey="composite_score" active={field} dir={dir} onToggle={toggle} align="right" className="w-20" />
              <SortHeader label="Entry" sortKey="suggested_entry" active={field} dir={dir} onToggle={toggle} align="right" className="w-24" />
              <SortHeader label="SL" sortKey="suggested_stop_loss" active={field} dir={dir} onToggle={toggle} align="right" className="w-24" />
              <SortHeader label="Target" sortKey="suggested_target" active={field} dir={dir} onToggle={toggle} align="right" className="w-24" />
              <SortHeader label="R:R" sortKey="risk_reward" active={field} dir={dir} onToggle={toggle} align="right" className="w-16" tooltip={RR_TOOLTIP} />
              <SortHeader label="M-Cap" sortKey="market_cap_cr" active={field} dir={dir} onToggle={toggle} align="right" className="w-24" />
              <th className="px-2 py-2 text-left min-w-[170px]">Factor Mix</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p, i) => {
              const isSelected = selectedSymbol === p.symbol;
              return (
                <tr
                  key={p.symbol}
                  onClick={() => onRowClick?.(p)}
                  className={`border-b border-border/50 cursor-pointer transition-colors ${
                    isSelected ? 'bg-accent/10' : 'hover:bg-surface-highlight'
                  }`}
                  data-testid={`pick-row-${p.symbol}`}
                >
                  <td className="px-2 py-2.5 text-muted-foreground font-mono text-xs">
                    {String(i + 1).padStart(2, '0')}
                  </td>
                  <td className="px-2 py-2.5">
                    <HoverCard openDelay={120} closeDelay={80}>
                      <HoverCardTrigger asChild>
                        <div
                          className="flex flex-col cursor-pointer group/sym"
                          data-testid={`pick-symbol-${p.symbol}`}
                        >
                          <div className="flex items-center gap-1.5">
                            <span className="font-bold text-sm text-foreground group-hover/sym:text-accent group-hover/sym:underline decoration-accent/60 underline-offset-2 transition-colors">
                              {p.symbol}
                            </span>
                            <EventBadge symbol={p.symbol} />
                          </div>
                          {p.name && (
                            <span className="text-[10px] text-muted-foreground truncate max-w-[130px]" title={p.name}>
                              {p.name.replace(' Limited', '').replace(' Ltd', '')}
                            </span>
                          )}
                        </div>
                      </HoverCardTrigger>
                      <HoverCardContent
                        side="right"
                        align="start"
                        sideOffset={8}
                        className="w-auto p-0 border-0 bg-transparent shadow-none"
                      >
                        <MiniFactorRadar pick={p} />
                      </HoverCardContent>
                    </HoverCard>
                  </td>
                  <td className="px-2 py-2.5" data-testid={`pick-status-${p.symbol}`}>
                    <ActionabilityBadge actionability={p.actionability} />
                  </td>
                  <td className="px-2 py-2.5 text-xs text-muted-foreground truncate max-w-[130px]" title={p.sector}>
                    {p.sector}
                  </td>
                  <td className="px-2 py-2.5 text-right">
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 rounded-sm text-xs font-bold border ${scoreColor(
                        p.composite_score
                      )}`}
                    >
                      {Number(p.composite_score).toFixed(1)}
                    </span>
                  </td>
                  <td className="px-2 py-2.5 text-right font-mono text-xs whitespace-nowrap">
                    ₹{formatMoney(p.suggested_entry)}
                  </td>
                  <td className="px-2 py-2.5 text-right font-mono text-xs text-red-400 whitespace-nowrap">
                    ₹{formatMoney(p.suggested_stop_loss)}
                  </td>
                  <td className="px-2 py-2.5 text-right font-mono text-xs text-emerald-400 whitespace-nowrap">
                    ₹{formatMoney(p.suggested_target)}
                  </td>
                  <td
                    className="px-2 py-2.5 text-right font-mono text-xs whitespace-nowrap"
                    title={
                      p.risk_reward
                        ? `Reward to Risk = ${p.risk_reward.toFixed(2)}:1. For every 1 INR risked, potential reward is ${p.risk_reward.toFixed(2)} INR.`
                        : RR_TOOLTIP
                    }
                  >
                    {p.risk_reward ? `${p.risk_reward.toFixed(2)}:1` : '–'}
                  </td>
                  <td className="px-2 py-2.5 text-right text-xs text-muted-foreground whitespace-nowrap">
                    {formatCr(p.market_cap_cr)}
                  </td>
                  <td className="px-2 py-2.5">
                    <div className="space-y-0.5">
                      {Object.entries(p.factor_scores || {}).map(([k, v]) => (
                        <FactorBar key={k} name={k} value={v} />
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PicksTable;
